/**
 * Frontend Request Obfuscation Utility
 * Generates obfuscated timestamps to prevent network request debugging
 * 
 * Security Features:
 * - Timestamp-based validation (2-minute window)
 * - Encoded and reversed random keys
 * - Base64 obfuscation
 * - Request replay protection
 */

class RequestObfuscation {
    private validationWindow: number;
    private salt: string;

    constructor() {
        // 2-minute validation window (in milliseconds)
        this.validationWindow = 2 * 60 * 1000;
        
        // Secret salt for additional obfuscation (must match backend)
        this.salt = 'YYZ_CBP_2024_SECURE_SALT';
    }

    /**
     * Generate obfuscated timestamp key for current time
     */
    generateKey(): string {
        const timestamp = Date.now();
        return this.encodeTimestamp(timestamp);
    }

    /**
     * Encode timestamp into obfuscated key
     */
    private encodeTimestamp(timestamp: number): string {
        try {
            // Step 1: Convert timestamp to string and add salt
            const timestampStr = timestamp.toString();
            const saltedTimestamp = timestampStr + this.salt;
            
            // Step 2: Base64 encode
            const base64Encoded = btoa(saltedTimestamp);
            
            // Step 3: Reverse the string
            const reversed = base64Encoded.split('').reverse().join('');
            
            // Step 4: Add random padding and encode again
            const padded = `${this.generateRandomString(3)}${reversed}${this.generateRandomString(4)}`;
            const finalEncoded = btoa(padded);
            
            return finalEncoded;
        } catch (error) {
            console.error('❌ Error encoding timestamp:', error);
            throw new Error('Failed to generate obfuscated key');
        }
    }

    /**
     * Decode obfuscated key back to timestamp (for testing/debugging)
     */
    private decodeKey(obfuscatedKey: string): number {
        try {
            // Step 1: Base64 decode
            const paddedReversed = atob(obfuscatedKey);
            
            // Step 2: Remove padding (first 3 chars, last 4 chars)
            const reversed = paddedReversed.slice(3, -4);
            
            // Step 3: Reverse back to original
            const base64Encoded = reversed.split('').reverse().join('');
            
            // Step 4: Base64 decode to get salted timestamp
            const saltedTimestamp = atob(base64Encoded);
            
            // Step 5: Remove salt to get original timestamp
            const timestampStr = saltedTimestamp.replace(this.salt, '');
            const timestamp = parseInt(timestampStr, 10);
            
            if (isNaN(timestamp)) {
                throw new Error('Invalid timestamp extracted from key');
            }
            
            return timestamp;
        } catch (error) {
            console.error('❌ Error decoding obfuscated key:', error);
            throw new Error('Invalid or corrupted obfuscated key');
        }
    }

    /**
     * Generate random string for padding
     */
    private generateRandomString(length: number): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    }

    /**
     * Generate headers object for HTTP requests
     */
    generateRequestHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
        const obfuscatedKey = this.generateKey();
        
        return {
            'X-Request-Key': obfuscatedKey,
            'X-RK': obfuscatedKey, // Backup header
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            ...additionalHeaders
        };
    }

    /**
     * Deobfuscate response data
     */
    deobfuscateResponse(obfuscatedData: any): any {
        try {
            if (!obfuscatedData.__obfuscated) {
                return obfuscatedData; // Not obfuscated
            }
            
            const decoded = atob(obfuscatedData.data);
            const [timestamp, encoded] = decoded.split(':');
            const jsonString = atob(encoded);
            
            return JSON.parse(jsonString);
        } catch (error) {
            console.error('❌ Error deobfuscating response:', error);
            return obfuscatedData; // Fallback to original data
        }
    }

    /**
     * Test the obfuscation system (development only)
     */
    test(): void {
        if (import.meta.env.MODE !== 'development') {
            return;
        }

        try {
            console.log('🔐 Testing request obfuscation system...');
            
            const testKey = this.generateKey();
            console.log('Generated key:', testKey);
            
            const decodedTimestamp = this.decodeKey(testKey);
            const currentTime = Date.now();
            const ageDifference = Math.abs(currentTime - decodedTimestamp);
            
            console.log('Decoded timestamp:', decodedTimestamp);
            console.log('Current timestamp:', currentTime);
            console.log('Age difference:', ageDifference, 'ms');
            console.log('Valid?', ageDifference <= this.validationWindow);
            console.log('✅ Obfuscation system test completed');
        } catch (error) {
            console.error('❌ Obfuscation system test failed:', error);
        }
    }
}

// Export singleton instance
export const requestObfuscation = new RequestObfuscation();

// Run test in development mode
if (import.meta.env.MODE === 'development') {
    requestObfuscation.test();
}